import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import DirectoryRowCard from "../components/DirectoryRowCard";
import { getAuthSession, updateAuthSession } from "../auth";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
} from "../components/saasStyles";

const initialCreateForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  roleId: "",
};

const initialEditForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  roleId: "",
  active: true,
};

const initialProfileForm = {
  firstName: "",
  lastName: "",
  email: "",
};

const initialPasswordForm = {
  currentPassword: "",
  newPassword: "",
};

export default function SettingsPage() {
  const session = getAuthSession();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [profileForm, setProfileForm] = useState(initialProfileForm);
  const [passwordForm, setPasswordForm] = useState(initialPasswordForm);
  const [search, setSearch] = useState("");
  const [quickAction, setQuickAction] = useState("default");
  const [message, setMessage] = useState(null);

  const loadData = async () => {
    const [usersResponse, rolesResponse, meResponse, auditResponse] = await Promise.all([
      api.get("/api/users"),
      api.get("/api/roles"),
      api.get("/api/me"),
      api.get("/api/audit-logs"),
    ]);
    setUsers(usersResponse.data);
    setRoles(rolesResponse.data);
    setAuditLogs(auditResponse.data);
    setProfileForm({
      firstName: meResponse.data.firstName || "",
      lastName: meResponse.data.lastName || "",
      email: meResponse.data.email || "",
    });
  };

  useEffect(() => {
    loadData().catch(() => setMessage({ type: "error", text: "Les parametres n'ont pas pu etre charges." }));
  }, []);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedUser(null);
      setEditForm(initialEditForm);
      return;
    }

    const user = users.find((entry) => entry.id === selectedUserId) || null;
    setSelectedUser(user);
    if (user) {
      setEditForm({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        email: user.email || "",
        password: "",
        roleId: String(user.role_id || ""),
        active: Boolean(user.active),
      });
    }
  }, [selectedUserId, users]);

  const filteredUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? users
      : users.filter((user) =>
          [user.first_name, user.last_name, user.email, user.role_label]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle)
        );

    if (quickAction === "active") return base.filter((user) => user.active);
    if (quickAction === "admins") return base.filter((user) => user.role_key === "admin");
    return base;
  }, [users, search, quickAction]);

  const handleCreate = async () => {
    try {
      await api.post("/api/users", {
        ...createForm,
        roleId: Number(createForm.roleId),
      });
      setCreateForm(initialCreateForm);
      setCreateOpen(false);
      await loadData();
      setMessage({ type: "success", text: "L'utilisateur a ete cree." });
    } catch (_error) {
      setMessage({ type: "error", text: "L'utilisateur n'a pas pu etre cree." });
    }
  };

  const handleSave = async () => {
    if (!selectedUserId) return;

    try {
      await api.patch(`/api/users/${selectedUserId}`, {
        ...editForm,
        roleId: Number(editForm.roleId),
      });
      await loadData();
      setMessage({ type: "success", text: "L'utilisateur a ete mis a jour." });
    } catch (_error) {
      setMessage({ type: "error", text: "L'utilisateur n'a pas pu etre enregistre." });
    }
  };

  const handleProfileSave = async () => {
    try {
      const response = await api.patch("/api/me", profileForm);
      updateAuthSession({
        token: response.data.token,
        name: response.data.user.name,
        email: response.data.user.email,
        role: response.data.user.roleLabel || response.data.user.role,
        roleKey: response.data.user.role,
      });
      await loadData();
      setMessage({ type: "success", text: "Le profil a ete mis a jour." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le profil n'a pas pu etre enregistre." });
    }
  };

  const handlePasswordChange = async () => {
    try {
      await api.post("/api/me/password", passwordForm);
      setPasswordForm(initialPasswordForm);
      await loadData();
      setMessage({ type: "success", text: "Le mot de passe a ete modifie." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le mot de passe n'a pas pu etre modifie." });
    }
  };

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Parametres"
      description="Vue des roles, des regles et de la configuration de la plateforme."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={() => setCreateOpen(true)} sx={saasHeroActionButtonSx}>
          + Nouvel utilisateur
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box sx={{ ...saasDirectoryWrapSx, borderRadius: "44px", px: { xs: 2, md: 2.5 }, py: { xs: 1.2, md: 1.4 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Afficher {filteredUsers.length} elements
        </Typography>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher un utilisateur"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              ...saasInputSx,
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                minHeight: 44,
                backgroundColor: "#fff",
                boxShadow: "none",
              },
            }}
            InputProps={{ startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} /> }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select value={quickAction} onChange={(event) => setQuickAction(event.target.value)} displayEmpty sx={{ minHeight: 56, borderRadius: "18px", bgcolor: "#fff", fontWeight: 700, boxShadow: "none" }}>
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="active">Actifs seulement</MenuItem>
              <MenuItem value="admins">Admins</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ...saasSoftCountSx, minWidth: 104, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>{filteredUsers.length} utilisateurs</Typography>
          </Box>

          <Box sx={{ width: 58, height: 44, borderRadius: "16px", border: "1px solid #dfe6f4", bgcolor: "#fff", display: "grid", placeItems: "center" }}>
            <Badge badgeContent={users.filter((user) => user.active).length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}><NotificationsRoundedIcon /></IconButton>
            </Badge>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.05fr 0.95fr" }, gap: 3 }}>
        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Utilisateurs</Typography>
              <Typography variant="body2" color="text.secondary">
                Vue structuree des roles, des comptes et du controle d'acces administrateur.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.25}>
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Visibles</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{filteredUsers.length}</Typography></Box></Box>
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Selection</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{selectedUserId ? 1 : 0}</Typography></Box></Box>
            </Stack>
          </Box>

          {filteredUsers.map((user) => (
            <DirectoryRowCard
              key={user.id}
              selected={selectedUserId === user.id}
              avatarText={`${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`}
              title={`${user.first_name} ${user.last_name}`}
              subtitle={`USER-${user.id}`}
              chips={[
                { label: user.role_label, color: "success" },
                { label: user.active ? "Actif" : "Inactif", color: "default" },
              ]}
              columns={[
                { label: "Compte", value: user.email, secondary: `Cree le ${new Date(user.created_at).toLocaleDateString("fr-FR")}` },
                { label: "Role", value: user.role_key, secondary: "Gestion administrative des utilisateurs" },
              ]}
              actionLabel={selectedUserId === user.id ? "Selectionne" : "Choisir"}
              onSelect={() => setSelectedUserId(user.id)}
            />
          ))}
        </Box>

        <Box sx={{ display: "grid", gap: 3 }}>
          <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 }, alignContent: "start" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Mon profil</Typography>
              <Typography variant="body2" color="text.secondary">Utilisateur actuel : {session?.email || "-"}</Typography>
            </Box>
            <TextField label="Prenom" value={profileForm.firstName} onChange={(event) => setProfileForm((current) => ({ ...current, firstName: event.target.value }))} sx={saasInputSx} />
            <TextField label="Nom" value={profileForm.lastName} onChange={(event) => setProfileForm((current) => ({ ...current, lastName: event.target.value }))} sx={saasInputSx} />
            <TextField label="E-mail" value={profileForm.email} onChange={(event) => setProfileForm((current) => ({ ...current, email: event.target.value }))} sx={saasInputSx} />
            <Button variant="contained" onClick={handleProfileSave} sx={{ borderRadius: "14px" }}>Enregistrer le profil</Button>

            <TextField label="Mot de passe actuel" type="password" value={passwordForm.currentPassword} onChange={(event) => setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }))} sx={saasInputSx} />
            <TextField label="Nouveau mot de passe" type="password" value={passwordForm.newPassword} onChange={(event) => setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))} sx={saasInputSx} />
            <Button variant="outlined" onClick={handlePasswordChange} sx={{ borderRadius: "14px" }}>Modifier le mot de passe</Button>
          </Box>

          <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 }, alignContent: "start" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Espace utilisateur</Typography>
              <Typography variant="body2" color="text.secondary">Edition du role, du statut actif et des acces.</Typography>
            </Box>

            {!selectedUser ? (
              <Typography variant="body2" color="text.secondary">Choisissez un utilisateur a gauche pour le modifier.</Typography>
            ) : (
              <>
                <TextField label="Prenom" value={editForm.firstName} onChange={(event) => setEditForm((current) => ({ ...current, firstName: event.target.value }))} sx={saasInputSx} />
                <TextField label="Nom" value={editForm.lastName} onChange={(event) => setEditForm((current) => ({ ...current, lastName: event.target.value }))} sx={saasInputSx} />
                <TextField label="E-mail" value={editForm.email} onChange={(event) => setEditForm((current) => ({ ...current, email: event.target.value }))} sx={saasInputSx} />
                <TextField select label="Role" value={editForm.roleId} onChange={(event) => setEditForm((current) => ({ ...current, roleId: event.target.value }))} sx={saasInputSx}>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>{role.label}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Nouveau mot de passe" type="password" value={editForm.password} onChange={(event) => setEditForm((current) => ({ ...current, password: event.target.value }))} sx={saasInputSx} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 700 }}>Actif</Typography>
                  <Switch checked={editForm.active} onChange={(event) => setEditForm((current) => ({ ...current, active: event.target.checked }))} />
                </Stack>
                <Button variant="contained" onClick={handleSave} sx={{ borderRadius: "14px" }}>Enregistrer l'utilisateur</Button>
              </>
            )}
          </Box>

          <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 1.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Journal d'audit</Typography>
              <Typography variant="body2" color="text.secondary">Dernieres actions utilisateur et profil.</Typography>
            </Box>
            {auditLogs.slice(0, 8).map((entry) => (
              <Box key={entry.id} sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.2, bgcolor: "#fdfefe" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#25324b" }}>{entry.action}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.2 }}>{entry.summary || "-"}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.2 }}>{entry.actorName} · {new Date(entry.createdAt).toLocaleString("fr-FR")}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Creer un utilisateur</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1.5 }}>
          <TextField label="Prenom" value={createForm.firstName} onChange={(event) => setCreateForm((current) => ({ ...current, firstName: event.target.value }))} sx={saasInputSx} />
          <TextField label="Nom" value={createForm.lastName} onChange={(event) => setCreateForm((current) => ({ ...current, lastName: event.target.value }))} sx={saasInputSx} />
          <TextField label="E-mail" value={createForm.email} onChange={(event) => setCreateForm((current) => ({ ...current, email: event.target.value }))} sx={saasInputSx} />
          <TextField label="Mot de passe" type="password" value={createForm.password} onChange={(event) => setCreateForm((current) => ({ ...current, password: event.target.value }))} sx={saasInputSx} />
          <TextField select label="Role" value={createForm.roleId} onChange={(event) => setCreateForm((current) => ({ ...current, roleId: event.target.value }))} sx={saasInputSx}>
            {roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>{role.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!createForm.firstName || !createForm.lastName || !createForm.email || !createForm.password || !createForm.roleId}>Creer l'utilisateur</Button>
        </DialogActions>
      </Dialog>
    </PageFrame>
  );
}
